import { ChangeEvent, useEffect, useRef, useState } from "react";
import axios from "axios";

export interface MediaFile {
    file: File;
    base64: string;
    name: string;
    caption: string;
}

interface SendMediaMessageProps {
    selectedFiles: MediaFile[];
    setSelectedFiles: React.Dispatch<React.SetStateAction<MediaFile[]>>;
    setFileSeleccionadoIndex: React.Dispatch<
        React.SetStateAction<number | null>
    >;
}

function SendMediaMessage({
    selectedFiles,
    setSelectedFiles,
    setFileSeleccionadoIndex,
}: SendMediaMessageProps) {
    const [isRecording, setIsRecording] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const [hasAudio, setHasAudio] = useState(false);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const total = selectedFiles.length + files.length;
        if (total > 50) {
            alert("Máximo 50 archivos.");
            return;
        }
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = (reader.result as string).split(",")[1];
                setSelectedFiles((prev) => [
                    ...prev,
                    { file, base64, name: file.name, caption: "" },
                ]);
            };
            reader.readAsDataURL(file);
        });
        e.target.value = "";
        setFileSeleccionadoIndex(0);
    };

    const removeFile = (index: number) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const hasAudio = selectedFiles.some((media) =>
            media.file.type.startsWith("audio/")
        );

        setHasAudio(hasAudio);
    }, [selectedFiles]);

    const startRecording = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            alert("Tu navegador no soporta grabación de audio.");
            return;
        }

        try {
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const descriptor = {
                        name: "microphone",
                    } as unknown as PermissionDescriptor;
                    const perm = await navigator.permissions.query(descriptor);
                    if (perm.state === "denied") {
                        alert(
                            "Has denegado el acceso al micrófono. " +
                                "Por favor activa el permiso desde la configuración de tu navegador."
                        );
                        return;
                    }

                    perm.onchange = () => {
                        if (
                            perm.state === "denied" &&
                            mediaRecorderRef.current
                        ) {
                            alert(
                                "Permiso del micrófono revocado. Se detendrá la grabación."
                            );
                            mediaRecorderRef.current.stop();
                        }
                    };
                } catch {}
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            streamRef.current = stream;

            const recorder = new MediaRecorder(stream);
            audioChunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            recorder.onstop = handleStop;
            recorder.start();
            mediaRecorderRef.current = recorder;

            setShowModal(true);
            setIsRecording(true);
        } catch (err: any) {
            console.error(err);
            if (
                err.name === "NotAllowedError" ||
                err.name === "PermissionDeniedError"
            ) {
                alert(
                    "No se pudo acceder al micrófono. Por favor acepta el permiso."
                );
            } else {
                alert("Error al arrancar la grabación.");
            }
        }
    };

    const stopRecording = () => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state !== "inactive"
        ) {
            mediaRecorderRef.current.stop();
        }
    };

    const handleStop = () => {
        setIsRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `grabacion_${Date.now()}.webm`, {
            type: blob.type,
        });
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = (reader.result as string).split(",")[1];
            setSelectedFiles((prev) => [
                ...prev,
                { file, base64, name: file.name, caption: "" },
            ]);
        };
        reader.readAsDataURL(blob);
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        mediaRecorderRef.current = null;
    };

    const toggleRecording = () => {
        if (isRecording) stopRecording();
        else startRecording();
    };

    return (
        <div className="flex items-start gap-2">
            {!hasAudio && (
                <label className="relative w-8 h-8 cursor-pointer">
                    <input
                        type="file"
                        multiple
                        accept="image/*,video/*,audio/*,.pdf"
                        className="w-full h-full opacity-0"
                        onChange={handleFileChange}
                        maxLength={50}
                    />
                    <div className="absolute inset-0 flex items-center justify-center transition-colors duration-200 bg-gray-200 rounded-full hover:bg-gray-300">
                        <svg
                            className="w-8 h-8 text-blue-500 cursor-pointer"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            {" "}
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                        </svg>
                    </div>
                </label>
            )}

            {!hasAudio && (
                <button
                    onClick={toggleRecording}
                    className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors duration-200 ${
                        isRecording
                            ? "bg-red-500 hover:bg-red-600"
                            : "bg-green-500 hover:bg-green-600"
                    }`}
                >
                    {isRecording ? (
                        <svg
                            className="w-6 h-6 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <rect x="6" y="6" width="12" height="12" />
                        </svg>
                    ) : (
                        <svg
                            className="w-6 h-6 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M12 1v11m-4-4a4 4 0 0 0 8 0" />
                            <path d="M19 11a7 7 0 0 1-14 0" />
                            <path d="M12 19v4" />
                            <path d="M8 23h8" />
                        </svg>
                    )}
                </button>
            )}

            {isRecording && <span className="text-red-500">Grabando...</span>}

            <div className=" max-h-20  overflow-y-auto flex flex-wrap gap-2">
                {selectedFiles.map((media, idx) => (
                    <div key={idx} className="relative">
                        {media.file.type.startsWith("image/") && (
                            <img
                                src={`data:${media.file.type};base64,${media.base64}`}
                                alt={media.name}
                                className="object-cover w-20 h-20 rounded"
                            />
                        )}
                        {media.file.type.startsWith("video/") && (
                            <video controls className="w-20 h-20 rounded">
                                <source
                                    src={`data:${media.file.type};base64,${media.base64}`}
                                    type={media.file.type}
                                />
                            </video>
                        )}
                        {media.file.type.startsWith("audio/") && (
                            <audio controls className="w-60 ">
                                <source
                                    src={`data:${media.file.type};base64,${media.base64}`}
                                    type={media.file.type}
                                />
                            </audio>
                        )}
                        {!["image/", "video/", "audio/"].some((t) =>
                            media.file.type.startsWith(t)
                        ) && <p className="text-sm">Archivo: {media.name}</p>}
                        <button
                            onClick={() => removeFile(idx)}
                            className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default SendMediaMessage;
